<template>
    <template v-if="!loading">
        <div class="mb-4">
            <div class="flex items-center justify-between">
                <span class="w-1/3 shrink-0"> {{ startItem }}-{{ endItem }} van {{ totalOrders }} </span>

                <UiPagination
                    class="hidden w-1/3 lg:block"
                    :current-page="currentPage"
                    :total-pages="totalPages"
                    :total-items="totalOrders"
                    @page-change="goToPage" />

                <div class="flex w-1/3 justify-end">
                    <UiSelect
                        id="orders-per-page-top"
                        v-model="itemsPerPage"
                        :options="perPageOptions"
                        placeholder="Bestellingen per pagina"
                        aria-label="Bestellingen per pagina"
                        class="w-full min-w-[10rem] shrink-0 sm:!w-52" />
                </div>
            </div>
        </div>
        <div v-if="paginatedOrders.length > 0">
            <div v-for="order in paginatedOrders" :key="order.id" class="mb-4 rounded-sm bg-green-300 p-6 lg:p-10">
                <div class="lg:flex lg:items-center lg:gap-16">
                    <div class="mb-5 lg:order-2 lg:mb-0 lg:flex-1">
                        <ShopwareAccountOrderImages :order="order" />
                    </div>
                    <!-- Fixed width so the image column starts at the same position on every card -->
                    <div class="mb-5 lg:order-1 lg:mb-0 lg:w-80 lg:shrink-0">
                        <p class="text-base tracking-[0.8px]">
                            <strong>Aankoopdatum:</strong> {{ formatDate(order.orderDate ?? order.orderDateTime) }}
                        </p>
                        <p class="text-base tracking-[0.8px]" v-if="!isInStoreOrder(order)">
                            <strong>Ordernummer:</strong> {{ order.orderNumber }}
                        </p>
                        <p class="text-base tracking-[0.8px]" v-if="!isInStoreOrder(order)">
                            <strong>Status:</strong> {{ orderStatus(order) }}
                        </p>
                        <p class="text-base tracking-[0.8px]" v-else>
                            <strong>{{ orderStatus(order) }}</strong>
                        </p>
                        <p class="text-base tracking-[0.8px]">
                            <strong>Aantal items:</strong>
                            {{ order.lineItems?.filter(item => item.type === 'product').length }}
                        </p>
                    </div>
                    <div class="lg:order-2">
                        <button
                            @click="invokeSidebar(order)"
                            class="btn mb-2 !w-full bg-green-600 text-white hover:bg-green-500 lg:mb-4"
                            aria-label="Bekijk aankoop details">
                            Bekijk aankoop
                        </button>
                    </div>
                </div>
            </div>

            <div class="mt-4">
                <div class="flex items-center justify-between">
                    <span class="w-1/3 shrink-0"> {{ startItem }}-{{ endItem }} van {{ totalOrders }} </span>
                    <UiPagination
                        class="hidden w-1/3 lg:block"
                        :current-page="currentPage"
                        :total-pages="totalPages"
                        :total-items="totalOrders"
                        @page-change="goToPage" />

                    <div class="flex w-1/3 justify-end">
                        <UiSelect
                            id="orders-per-page-bottom"
                            v-model="itemsPerPage"
                            :options="perPageOptions"
                            placeholder="Bestellingen per pagina"
                            aria-label="Bestellingen per pagina"
                            class="w-full min-w-[10rem] shrink-0 sm:!w-52" />
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-green-300 p-10" v-else>
            <h3 class="text-xl">{{ noOrdersTitle }}</h3>
            <p>{{ noOrdersDescription }}</p>
            <NuxtLink class="btn mt-4 w-fit bg-green-600 text-white hover:bg-green-500" to="/nl/">{{
                checkProducts
            }}</NuxtLink>
        </div>
    </template>
    <template v-else>
        <div v-for="index in 4" class="mb-3 animate-pulse bg-gray-200 p-6 lg:p-7" :key="index">
            <div class="lg:flex lg:items-center lg:gap-16">
                <div class="mb-5 lg:order-2 lg:mb-0 lg:flex-1">
                    <div class="grid grid-cols-4 gap-2 lg:flex lg:gap-0">
                        <div class="bg-white py-2 lg:mr-10 lg:size-40 lg:py-6"></div>
                        <div class="bg-white py-2 lg:mr-10 lg:size-40 lg:py-6"></div>
                        <div class="bg-white py-2 lg:mr-10 lg:size-40 lg:py-6"></div>
                        <div class="bg-white py-2 lg:mr-10 lg:size-40 lg:py-6"></div>
                    </div>
                </div>
                <div class="mb-5 lg:order-1 lg:mb-0 lg:w-80 lg:shrink-0">
                    <div class="h-4 w-52 rounded bg-white"></div>
                    <div class="mt-2 h-4 w-3/4 rounded bg-white"></div>
                </div>
                <div class="lg:order-2">
                    <div class="mt-2 h-12 w-52 rounded bg-white"></div>
                    <div class="mt-2 h-12 w-full rounded bg-white"></div>
                </div>
            </div>
        </div>
    </template>

    <ShopwareSidebar @close="invokeSidebar('')" :open="openSidebar">
        <template #title>Mijn aankoop</template>
        <template #content>
            <div class="p-4">
                <ShopwareAccountOrderDetails :orderDetails="orderDetails" />
            </div>
        </template>
    </ShopwareSidebar>
</template>

<script lang="ts" setup>
    import type { Order } from '@shopware-pwa/types';

    const props = defineProps({
        activeTab: {
            type: String,
            default: 'Alles',
        },
    });

    const { orders, loadOrders, limit } = useCustomerOrders();
    const { snippets } = useSnippetsStore();
    const loading = ref(true);
    const openSidebar = ref(false);
    const orderDetails = ref([]);

    // Get specific snippets to avoid TypeScript errors with index access
    const noOrdersTitle = computed(() => snippets?.['headless.account.noOrdersTitle'] || 'Geen bestellingen gevonden');
    const noOrdersDescription = computed(
        () => snippets?.['headless.account.noOrdersDescription'] || 'Je hebt nog geen bestellingen geplaatst.',
    );
    const checkProducts = computed(() => snippets?.['headless.account.checkProducts'] || 'Bekijk producten');

    // In-store (TritonX) orders are synthetic and only they carry the tritonx_order custom-field block
    function isInStoreOrder(order: any): boolean {
        return !!order.customFields?.tritonx_order;
    }

    const filteredOrders = computed(() => {
        if (props.activeTab === 'Winkel') return orders.value.filter(isInStoreOrder);
        if (props.activeTab === 'Online') return orders.value.filter((order: any) => !isInStoreOrder(order));
        return orders.value;
    });

    // Pagination related refs
    const currentPage = ref(1);
    const itemsPerPage = ref<number>(10); // Default to 10 orders per page
    const totalOrders = computed(() => filteredOrders.value.length);
    const totalPages = computed(() => Math.ceil(totalOrders.value / itemsPerPage.value));

    // Options for per page select
    const perPageOptions = [
        { value: 5, label: '5 per pagina' },
        { value: 10, label: '10 per pagina' },
        { value: 20, label: '20 per pagina' },
    ];

    // Calculate start and end items for display in title
    const startItem = computed(() => {
        if (totalOrders.value === 0) return 0;
        return (currentPage.value - 1) * itemsPerPage.value + 1;
    });

    const endItem = computed(() => {
        const end = currentPage.value * itemsPerPage.value;
        return end > totalOrders.value ? totalOrders.value : end;
    });

    // Get paginated orders
    const paginatedOrders = computed(() => {
        const startIndex = (currentPage.value - 1) * itemsPerPage.value;
        const endIndex = startIndex + itemsPerPage.value;
        return filteredOrders.value.slice(startIndex, endIndex);
    });

    function goToPage(page: number) {
        currentPage.value = page;
        scrollToTop();
    }

    function scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function invokeSidebar(order: any) {
        orderDetails.value = order;
        openSidebar.value = !openSidebar.value;
    }

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        const day = date.getDate();
        const year = date.getFullYear();

        const monthFormatter = new Intl.DateTimeFormat('nl', { month: 'long' });
        const month = monthFormatter.format(date);

        return `${day} ${month} ${year}`;
    }

    // TritonX in-store orders are synthetic and carry no state machine; show a clear label instead.
    function orderStatus(order: any): string {
        if (isInStoreOrder(order)) {
            return snippets?.['headless.account.inStorePurchase'] || 'In winkel gekocht';
        }

        return order.stateMachineState?.translated?.name ?? '';
    }

    // Watch for changes to items per page to reset current page
    watch(itemsPerPage, () => {
        currentPage.value = 1;
    });

    // Reset pagination when switching tabs
    watch(
        () => props.activeTab,
        () => {
            currentPage.value = 1;
        },
    );

    try {
        limit.value = 100;
        await loadOrders({
            associations: {
                lineItems: {
                    associations: {
                        cover: {},
                    },
                },
                transactions: {},
                deliveries: {
                    associations: {
                        shippingOrderAddress: {},
                    },
                },
                stateMachineState: {},
            },
            sort: [
                {
                    field: 'orderDateTime',
                    order: 'DESC',
                },
            ],
            filter: [
                {
                    type: 'multi',
                    operator: 'and',
                    queries: [
                        {
                            type: 'not',
                            queries: [
                                {
                                    type: 'equals',
                                    field: 'stateMachineState.technicalName',
                                    value: 'cancelled',
                                },
                            ],
                        },
                    ],
                },
            ],
        }).then(async () => {
            loading.value = false;
        });
    } catch (error) {
        console.error('Error loading orders:', error);
        loading.value = false;
    }
</script>
